// The ONE asset queue (MAPFIRST, APP_PLAYER §7): every fetch of a bake
// artifact goes through fetchAsset, so priorities, pausing and the load
// report's per-class timings live in one place — and a later service worker
// (map tiles) or worker executor slots in UNDER this seam without touching
// the call sites.
//
//   class  'map'  — the 2D Map's class map (coarse.png, genome, class tiles, the Map's GROUND-M level): high
//          'near' — the ground at a target (GM L0 nodes, terrain rings, building
//                   collision): high, never queued here (the terrain keeps
//                   its own priority queue; a newer card retargets it)
//          'bulk' — location-independent phase-1 content (ways, trees,
//                   shrubs, objects, lifts, water, rivers, surface, ring,
//                   gazetteer, horizon…): low, ≤ BULK_CAP in flight, and
//                   held while any pause reason is set; ORDERED BY NEED
//   need   (bulk only) {rect, m, bytes} — else the installed resolver's
//          answer for the URL (main.js: needscore.js needResolver(meta))
//   as     'json' | 'text' | 'arrayBuffer' | 'blob' (the body type)
//
// Resolves the body, or null when the response is not ok (the
// `r.ok ? r.x() : null` every call site used); rejects on a network error,
// exactly like fetch. `status: true` resolves {status, body} instead (body
// null unless ok) for the one caller that tells a 404 from other failures
// (surface.js). Uses the fetch `priority` hint where the browser has
// it (Chromium); elsewhere the hint is ignored and the queue still orders.
//
// Pausing: pauseBulk(reason) / resumeBulk(reason) — queued bulk waits while
// ANY reason is set, in-flight requests finish. main.js: 'beam' from the ⤳
// Beam click to the beam-in latch (the near ground gets the pipe), 'save-data'
// (navigator.connection.saveData) from boot until the first card opens.
// THE TRANSPORT (content-addressed objects): callers keep the LOGICAL url
// `/bake/<id>/<rel>` (families, need lookup, accounting, act labels); only the
// one network call goes to `/o/<sha256>` when the world manifest (WORLD2: main.js
// merges its family indexes into configureAssetQueue manifest {base, files}) names that rel — the same bytes
// for every bake that shares them, so a new build costs only what changed
// (client/sw.js keeps /o/ cache-first). Not in the manifest: the url as is.
// transportUrl / logicalUrl convert both ways (raw dev fetches, resource timing).
//
// The need score (stream-need-score, needscore.js): the next bulk fetch is
// the queued one with the best rank at the current view — in view first,
// then projected pixels / bytes, then the request order. setNeedView(view)
// (main.js needTick: a target change — a card, the Beam, a GPS fix — a move,
// a turn, a zoom) re-scores the queue; a queued asset whose whole extent is
// within one pixel there is DROPPED: never fetched, resolves null (status:
// {status: 0, body: null, dropped: true, gen}). No view (Map first: no target
// yet) = one score for all = FIFO, the boot order. map / near never queue,
// never score, never drop: the Map, and what the player needs to STAND.
//
// A drop is not a failure (need-drop-rerequest): it is the view's verdict,
// so only a NEW view can undo it. `status: true` callers tell it from a 404
// by `dropped` and read `gen`, the view generation it dropped at (needGen);
// onNeedView(cb) runs cb after every re-score — the consumers (ring.js tiled
// sectors, surface.js far slots) ask a dropped asset they still want again
// there, once per view (at the same gen it would drop again: no storm).
// Without `status` a drop still resolves null, exactly like a 404.
//
// STRICT PRIORITY (download-order): while any `map` or `near` request is in
// flight no new `bulk` fetch STARTS (in-flight bulk finishes); the queue
// resumes when they drain. On a thin line (5 Mbit/s) a 40 kB Map photo tile
// otherwise shares the pipe with four multi-MB strips and takes seconds.
// The pause reasons hold on top of it.
//
// THE LANE (startup-horizon): a family the need config names (ringtiers.json
// need.lane {fam, cap, through}; shipped: the far ring) has `cap` slots of its
// own on top of BULK_CAP. They take that family's queued jobs best-ranked
// first (needCmp, so the view's own sectors lead). Strict priority does not
// hold them, nor a pause reason listed in `through` ('beam'). They keep the
// 'low' hint, so the browser still serves map / near first. The far ring is
// the first thing the eye sees and its files are 4-140 KB; behind the 'beam'
// pause they waited the whole latch (33-56 s, load reports 2026-10-24). No
// lane configured (the gates' default, a needCfg without `lane`): none.
//
// THE FIRST FAMILIES (beam-order; ringtiers.json need.first, shipped: the
// ground material arrays): a queued bulk job of a family `first` names starts
// before every other bulk job (aged ones too; among themselves by needCmp).
// Pauses and strict priority hold them like the rest. Scored by pixels per
// byte, the 1.1 MB arrays came last behind the canopy / forest / shd2
// backlog the latch releases: queued at the latch + 1.3 s, they started at
// +39-44 s, at or after overlay-off (the first view swapped plain ground for
// them in play; probe 2026-10-24, desktop-igpu). No `first` = none. Shipped
// too: `veg`, the owner's tree / bush sprites (veg/<sha16>.webp, 0.1-0.3 MB
// each, 4 on the reference bake) — the overlay waits for them (main.js THE
// SPRITE GATE), so behind the backlog they would hold it.
//
// THE LOOK TIER (needq-near-first; ringtiers.json need.look {file, tiled, m,
// lane}): what PAINTS the near view goes before what only fills the distance.
// A bulk job is `look` when its artifact is named in `file` (the ground
// painter's recipe, surface/genome.json: world-wide, unscored, so its score
// was ~2 px/byte, behind every tile) or its family is in `tiled` and its
// footprint is within `m` metres of the view's eye (near crowns / shrubs /
// ways / surface tiles; re-judged at every re-score). A look job starts before
// every other bulk job (aged ones too) except a first family's, in the need
// order among themselves; far ring sectors and far way tiles are never look. A
// look job that is small (`file`, or a known need.bytes <= lane.max_b) also
// has `lane.cap` slots of its own, past strict priority and the `lane.through`
// pause reasons ('beam'): the genome and the near packs no longer wait out the
// Beam -> latch pause (12-13 s in the load reports 2026-10-30, ids 40952 /
// 40945) behind nothing but their own queue. Big look files (the 1.3 MB ground
// material arrays) keep the pause: they would take the near ground's pipe.
// No `look` = none (the gates' default).
//
// ROBUST DOWNLOADS: every class, every request — a request with no bytes of
// progress (headers, then each body chunk) for STALL_MS is aborted; a
// network error, a stall or a 408 / 425 / 429 / 5xx answer is retried up to
// TRIES in all, after BACKOFF_MS. The last network error / stall rejects
// with an Error naming the file (callers already handle a rejection); the
// last non-ok answer resolves null as ever. Each retry and each give-up is a
// console.warn naming the file; the counts are needReport's retry_n /
// stall_n / fail_n. A bulk request keeps its slot through its backoff.
//
// No DOM at module scope: scripts/check-mapfirst.mjs and
// scripts/check-needscore.mjs drive it in Node.
//
// Long-task attribution (sched.js act): a completion marks `fetch:<artifact>`
// — the caller's continuation runs in the same task — and a json body is
// parsed under `parse:<artifact>`.
//
// BIG JSON IN STEPS (smooth Map): a json body of ≥ JSON_STEPS_MIN chars
// (buildings/registry.json, groundm/index.json, rivers, objects, water,
// meta) is parsed by the installed step parser (configureAssetQueue
// parseJson — main.js: decode.js runSteps(parseSteps(text), label), the
// same value as JSON.parse, sliced at the phase budget) instead of one
// JSON.parse task; a smaller one, or none installed (the gates' Node
// runs), one JSON.parse as before. Not a worker: handing a parsed object
// back is a structured-clone task as long as the parse (APP_STARTUP).
import { act, actLabel } from './sched.js';
import { needOf, needCmp, needAged } from './needscore.js';

const HINT = { map: 'high', near: 'high', bulk: 'low' };
const CLASSES = ['map', 'near', 'bulk'];
let BULK_CAP = 4;
const LOG_CAP = 6000;
let STALL_MS = 20000;         // no progress this long: abort (then retry)
let TRIES = 3;                // attempts per request, all classes
let BACKOFF_MS = [1000, 4000];   // the wait before retry k (the last repeats)
const RETRY_ST = new Set([408, 425, 429, 500, 502, 503, 504]);
// a status assetq retries (and, past its tries, gives up on loudly): not a
// deterministic answer — a caller may ask again later
export const retryable = (st) => RETRY_ST.has(st);

let _fetch = (url, init) => fetch(url, init);
// THE fetch() CALL TIMES (gs-gpupainter ruling p7 (4)): every main-thread call of the transport, the
// bare _fetch(url, init) alone (the init made before it) on the real clock; span = the synchronous
// section around it (go(): the queue's record, _get / _once up to and past the call), so a slow call
// shows whether the time is the browser's own (ms ~ span) or the code around it's (span >> ms)
const _pnow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const FETCH_SLOW_MS = 20;
const _fc = { n: 0, ms: 0, max: 0, slow: 0, span_max: 0, kinds: {}, last: [] };
let _fcLast = null;
function _fcAdd(url, cls, ms) {
  const kind = famOf(url) || 'other', K = _fc.kinds[kind] || (_fc.kinds[kind] = { n: 0, max: 0, slow: 0 });
  _fc.n++; _fc.ms += ms; K.n++;
  if (ms > _fc.max) _fc.max = ms;
  if (ms > K.max) K.max = ms;
  if (ms > FETCH_SLOW_MS) { _fc.slow++; K.slow++; }
  _fcLast = { kind, cls, ms: Math.round(ms * 100) / 100, span: null, at: Math.round(_pnow()) / 1000, url: artOf(url) };
  if (ms > 5) { _fc.last.push(_fcLast); if (_fc.last.length > 16) _fc.last.shift(); }
}
// {n, ms total, max, slow (> FETCH_SLOW_MS), span_max, kinds {family: {n, max, slow}}, last: the calls > 5 ms}
export function fetchCallStats() {
  const r = (v) => Math.round(v * 100) / 100;
  return { slow_ms: FETCH_SLOW_MS, n: _fc.n, ms: r(_fc.ms), max: r(_fc.max), slow: _fc.slow, span_max: r(_fc.span_max),
    kinds: Object.fromEntries(Object.entries(_fc.kinds).map(([k, v]) => [k, { n: v.n, max: r(v.max), slow: v.slow }])), last: _fc.last.map((x) => ({ ...x })) };
}
const JSON_STEPS_MIN = 1 << 18;
let _parseJson = null;        // (text, label) => Promise<value> (configureAssetQueue parseJson)
const _paused = new Set();
const _bulkQ = [];            // [{run, seq, need, k, rec, drop}] ranked by needCmp
let _bulkIn = 0, _seq = 0;
let _laneIn = 0;              // lane jobs in flight (THE LANE)
let _lookIn = 0;              // look-lane jobs in flight (THE LOOK TIER)
let _hiIn = 0;                // map / near in flight (strict priority)
const _net = { retry: 0, stall: 0, fail: 0 };
const _log = [];              // [{url, cls, q, t0, t1, bytes, ok, s, v, dropped, tries, err}]
let _now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
let _needFor = null;          // url -> need | null (needscore.js needResolver)
let _needCfg = null;          // ringtiers.json need
let _view = null;             // the view bulk is scored at (null: no target yet)
let _rescores = 0;             // = the view generation (needGen)
const _viewCbs = new Set();   // onNeedView listeners

// test / future-executor hook: swap the transport, set the bulk cap; the
// need resolver + its config (main.js after meta); `now` = the gates' clock;
// stallMs / tries / backoffMs: the robust-download knobs (the gates' small ones)
export function configureAssetQueue({ fetch: f, bulkCap, need, needCfg, now, stallMs, tries, backoffMs, parseJson, manifest } = {}) {
  if (f) _fetch = f;
  if (manifest !== undefined) _setManifest(manifest);
  if (parseJson !== undefined) _parseJson = typeof parseJson === 'function' ? parseJson : null;
  if (Number.isInteger(bulkCap) && bulkCap > 0) BULK_CAP = bulkCap;
  if (stallMs > 0) STALL_MS = stallMs;
  if (Number.isInteger(tries) && tries > 0) TRIES = tries;
  if (Array.isArray(backoffMs) && backoffMs.length && backoffMs.every((x) => x >= 0)) BACKOFF_MS = backoffMs.slice();
  if (need !== undefined) _needFor = typeof need === 'function' ? need : null;
  if (needCfg !== undefined) _needCfg = needCfg || null;
  if (typeof now === 'function') _now = now;
}

// THE TRANSPORT: the world manifest {base: '/bake/<id>', files: {rel: [sha, bytes, br]}} or null
const SHA_RE = /^[0-9a-f]{64}$/;
const OBJ_RE = /^(.*?)\/o\/([0-9a-f]{64})(?:[?#].*)?$/;
let _man = null;
let _bySha = null;            // sha -> rel, built by indexSteps / on the first logicalUrl
let _idx = null;              // that build in progress {files, keys, k, map} (indexSteps)
function _setManifest(m, alias = []) {
  // `root` = the base with its trailing slash: every manifest name sits under it
  _man = m && typeof m.base === 'string' && m.files && typeof m.files === 'object'
    ? { root: m.base.replace(/\/?$/, '/'), files: m.files, alias } : null;
  _bySha = null;
  _idx = null;
}
// A WORLD SWAP (worldswap.js, R8h1): the live world moved under a running
// client and it keeps running — the manifest becomes the new world's {base
// '/bake/<new id>', files}. logicalUrl names the new base from here on; a url
// of an earlier base (main.js holds its boot `baseUrl` in closures, a job
// queued before the swap) still resolves, through the NEW files: the caller
// decided the rels it reads are the same objects (worldswap.js decideSwap).
// A request already in flight keeps its /o/<sha> (content-addressed: the same
// bytes). No manifest yet = configure it (the boot path).
export function replaceManifest(m) {
  const prev = _man;
  const alias = prev ? [...prev.alias, prev.root] : [];
  _setManifest(m, alias);
  if (_man) _man.alias = alias.filter((r) => r !== _man.root);
  return !!_man;
}
const _shaOf = (e) => { const s = Array.isArray(e) ? e[0] : e; return typeof s === 'string' && SHA_RE.test(s) ? s : null; };
// the url the network sees: `/o/<sha>` for a manifest file of the base (or of
// an earlier one, replaceManifest), else `url`
export function transportUrl(url) {
  const rel = _relOf(url);
  const sha = rel !== null && Object.prototype.hasOwnProperty.call(_man.files, rel) ? _shaOf(_man.files[rel]) : null;
  return sha ? '/o/' + sha : url;
}
// whether the world has the file `url` names: a url under a base (or an
// earlier one) only when the manifest lists its rel; outside every base, or
// no manifest yet, always (the caller cannot know better than the server)
export function listed(url) {
  const rel = _relOf(url);
  return rel === null || Object.prototype.hasOwnProperty.call(_man.files, rel);
}
// `url` -> its rel under the manifest's base or an earlier one, else null
function _relOf(url) {
  if (!_man || typeof url !== 'string') return null;
  const root = url.startsWith(_man.root) ? _man.root : _man.alias.find((r) => url.startsWith(r));
  return root ? url.slice(root.length).replace(/[?#].*$/, '') : null;
}
// the reverse (resource-timing names, absolute ones too): `[origin]/o/<sha>` ->
// `[origin]/bake/<id>/<rel>` (a sha several rels share: the first); else `u`.
// The map is keyed by each entry's raw sha field, no SHA_RE test (R8d6 item
// 5: the regex over every manifest file was a fifth of the load report's
// task): only a 64-hex key can equal OBJ_RE's m[2], so every lookup answers
// as a map of the valid shas would
export function logicalUrl(u) {
  const m = _man && typeof u === 'string' ? OBJ_RE.exec(u) : null;
  if (!m) return u;
  if (!_bySha) _indexMore(Infinity);
  const rel = _bySha.get(m[2]);
  return rel !== undefined ? m[1] + _man.root + rel : u;
}
// the reverse map's build, `n` more manifest entries (in Object.keys order:
// the first rel of a sha wins); complete -> _bySha
function _indexMore(n) {
  if (!_idx) _idx = { files: _man.files, keys: Object.keys(_man.files), k: 0, map: new Map() };
  const { files, keys, map } = _idx, end = Math.min(keys.length, _idx.k + n);
  for (let k = _idx.k; k < end; k++) {
    const rel = keys[k], e = files[rel], s = Array.isArray(e) ? e[0] : e;
    if (!map.has(s)) map.set(s, rel);
  }
  _idx.k = end;
  if (end === keys.length) { _bySha = map; _idx = null; }
}
// THE REVERSE MAP IN STEPS (R8 M5c; main.js runs it under 'assetq.index'
// once heavyReady opens): INDEX_STEP manifest entries a step, so the load
// report's first logicalUrl finds it built (it was 26 ms of that report's
// task over 21,620 entries). A logicalUrl before it is done finishes the
// same build at once; a manifest replaced meanwhile restarts it
export const INDEX_STEP = 2048;
export function* indexSteps(step = INDEX_STEP) {
  while (_man && !_bySha) {
    _indexMore(step);
    yield _idx ? _idx.k : null;
  }
}

// score job j at the current view; a drop resolves it now (never fetched)
function _score(j) {
  j.k = needOf(j.need, _view, _needCfg);
  j.rec.s = j.k.score;
  j.rec.v = j.k.inView;
  j.look = _lookOf(j);
  return j.k.drop;
}

// the view the bulk queue is ranked at (needscore.js: {eye, pa, px, aspect,
// dir} or null): re-scores every queued fetch, drops the sub-pixel ones
export function setNeedView(view) {
  _view = view || null;
  _rescores++;
  for (let i = _bulkQ.length - 1; i >= 0; i--) {
    if (_score(_bulkQ[i])) _bulkQ.splice(i, 1)[0].drop();
  }
  // the consumers re-ask what they dropped and still want: scored (and
  // ranked) at this view on enqueue, before the pump picks the next one
  for (const cb of _viewCbs) {
    try { cb(_rescores); } catch (e) { console.error(e); }
  }
  _pump();
}
// the view generation: +1 per setNeedView; a drop carries the one it fell at
export const needGen = () => _rescores;
// cb(gen) after every re-score; returns the unsubscribe
export function onNeedView(cb) {
  _viewCbs.add(cb);
  return () => _viewCbs.delete(cb);
}

// the absolute url a resource-timing entry names (assetStats matches by it):
// resolved once at the request, not per log row in the report's task
const _abs = (u) => { try { return new URL(u, location.href).href; } catch { return u; } };

export function fetchAsset(url, { prio = 'bulk', as = 'arrayBuffer', status = false, need } = {}) {
  const cls = CLASSES.includes(prio) ? prio : 'bulk';
  const rec = { url, abs: _abs(url), cls, q: _now(), t0: null, t1: null, bytes: 0, ok: false, s: null, v: null, dropped: false, tries: 0, err: null };
  if (_log.length < LOG_CAP) _log.push(rec);
  const go = () => {
    const s0 = _pnow();
    _fcLast = null;
    rec.t0 = _now();
    // map / near hold the bulk queue while in flight (strict priority)
    const hi = cls !== 'bulk';
    if (hi) _hiIn++;
    const g = _get(url, cls, as, rec);
    // the synchronous section around this go()'s fetch() call (fetchCallStats span)
    if (_fcLast) { const sp = _pnow() - s0; _fcLast.span = Math.round(sp * 100) / 100; if (sp > _fc.span_max) _fc.span_max = sp; _fcLast = null; }
    const p = g.then(({ st, raw }) => {
      rec.t1 = _now();
      rec.ok = raw !== null;
      if (raw) rec.bytes = raw.byteLength ?? raw.size ?? (typeof raw === 'string' ? raw.length : 0);
      const done = (body) => {
        act(actLabel('fetch', url));   // from here: the caller's continuation (same task)
        return status ? { status: st, body } : body;
      };
      if (raw === null || as !== 'json') return done(raw);
      const body = _json(raw, actLabel('parse', url));
      return body instanceof Promise ? body.then(done) : done(body);   // in steps: a promise
    }, (e) => { rec.t1 = _now(); throw e; });
    if (hi) p.then(_hiDone, _hiDone);
    return p;
  };
  if (cls !== 'bulk') return go();
  return new Promise((resolve, reject) => {
    const j = {
      run: () => go().then(resolve, reject), seq: _seq++, rec, k: null,
      need: need !== undefined ? need : (_needFor ? _needFor(url) : null),
      // never fetched: the body a non-ok answer gives, flagged
      drop: () => {
        rec.dropped = true;
        rec.nb = (j.need && j.need.bytes > 0) ? j.need.bytes : 0;
        rec.t0 = rec.t1 = _now();
        resolve(status ? { status: 0, body: null, dropped: true, gen: _rescores } : null);
      },
    };
    if (_score(j)) { j.drop(); return; }
    _bulkQ.push(j);
    _pump();
  });
}

// a json body: in steps when big and a step parser is installed, else one
// JSON.parse (exported: fetchonce.js parses its shared text the same way)
export function parseJsonBody(raw, label) { return _json(raw, label); }
function _json(raw, label) {
  if (_parseJson && raw.length >= JSON_STEPS_MIN) return _parseJson(raw, label);
  return act(label, () => JSON.parse(raw));
}

function _hiDone() {
  if (--_hiIn === 0) _pump();
}

function _pump() {
  const L = _lane(), K = _lookLane();
  const main = !_paused.size && !_hiIn && _bulkIn < BULK_CAP;
  const open = (l, n) => !!l && n < l.cap && ![..._paused].some((r) => !l.through.includes(r));
  const lane = open(L, _laneIn), look = open(K, _lookIn);
  if ((!main && !lane && !look) || !_bulkQ.length) return;
  const t = _now();
  for (const j of _bulkQ) j.old = needAged(j.rec.q, t, _needCfg);
  const F = _firstFams();
  while (!_paused.size && !_hiIn && _bulkIn < BULK_CAP && _bulkQ.length) {
    let b = 0;
    for (let i = 1; i < _bulkQ.length; i++) if (_firstCmp(_bulkQ[i], _bulkQ[b], F) < 0) b = i;
    const j = _bulkQ.splice(b, 1)[0];
    _bulkIn++;
    j.run().finally(() => { _bulkIn--; _pump(); });
  }
  // THE LOOK TIER's lane, then THE LANE: their own slots, past strict priority and the `through` reasons
  if (look) {
    while (_lookIn < K.cap) {
      const j = _laneTake((q) => q.look === 2);
      if (!j) break;
      j.rec.lane = 'look';
      _lookIn++;
      j.run().finally(() => { _lookIn--; _pump(); });
    }
  }
  if (!lane) return;
  while (_laneIn < L.cap) {
    const j = _laneTake((q) => {
      if (q.fam === undefined) q.fam = famOf(q.rec.url);
      return L.fam.includes(q.fam);
    });
    if (!j) return;
    j.rec.lane = true;
    _laneIn++;
    j.run().finally(() => { _laneIn--; _pump(); });
  }
}
// the best-ranked (needCmp) queued job `ok` accepts, taken out of the queue; null if none
function _laneTake(ok) {
  let b = -1;
  for (let i = 0; i < _bulkQ.length; i++) {
    if (ok(_bulkQ[i]) && (b < 0 || needCmp(_bulkQ[i], _bulkQ[b]) < 0)) b = i;
  }
  return b < 0 ? null : _bulkQ.splice(b, 1)[0];
}
// the need config's first families ([family]) or null; a job's family once (j.fam)
function _firstFams() {
  const F = _needCfg && _needCfg.first;
  return Array.isArray(F) && F.length ? F : null;
}
function _isFirst(j, F) {
  if (j.fam === undefined) j.fam = famOf(j.rec.url);
  return F.includes(j.fam) ? 1 : 0;
}
// a first family's job before any other, then a look job (THE LOOK TIER), then the need order
const _firstCmp = (a, b, F) => (F ? _isFirst(b, F) - _isFirst(a, F) : 0) || ((b.look > 0) - (a.look > 0)) || needCmp(a, b);
// the need config's lane {fam: [family], cap, through: [pause reason]} or null
function _lane() {
  const L = _needCfg && _needCfg.lane;
  if (!L || !Array.isArray(L.fam) || !L.fam.length || !(Number.isInteger(L.cap) && L.cap > 0)) return null;
  return { fam: L.fam, cap: L.cap, through: Array.isArray(L.through) ? L.through : [] };
}

// the need config's look tier {file, tiled, m, lane} or null (no `look`, or m not > 0)
function _look() {
  const C = _needCfg && _needCfg.look;
  if (!C || !(C.m > 0)) return null;
  return { file: Array.isArray(C.file) ? C.file : [], tiled: Array.isArray(C.tiled) ? C.tiled : [], m: C.m };
}
// the look tier's lane {cap, through, max_b} or null
function _lookLane() {
  const C = _needCfg && _needCfg.look, L = C && _look() && C.lane;
  if (!L || !(Number.isInteger(L.cap) && L.cap > 0)) return null;
  return { cap: L.cap, through: Array.isArray(L.through) ? L.through : [], max_b: L.max_b > 0 ? L.max_b : 0 };
}
// job j's look level at the view it was just scored at: 0 not look, 1 look, 2
// look and small enough for the look lane. Scored once per view (_score), no
// view = a named file only (the distance is unknown).
function _lookOf(j) {
  const C = _look();
  if (!C) return 0;
  if (j.fam === undefined) j.fam = famOf(j.rec.url);
  if (C.file.includes(artOf(j.rec.url))) return 2;
  if (!C.tiled.includes(j.fam) || j.k.d === null || !(j.k.d <= C.m)) return 0;
  const K = _lookLane(), b = j.need && j.need.bytes;
  return K && K.max_b > 0 && b > 0 && b <= K.max_b ? 2 : 1;
}

const _sleep = (ms) => new Promise((r) => { const h = setTimeout(r, ms); if (h && h.unref) h.unref(); });

// one request with the stall watch: -> {st, raw} (raw null unless ok); a
// stall aborts it and rejects (a transport that ignores the signal — the
// gates' — is left behind, never awaited)
function _once(url, cls, as, rec) {
  const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
  return new Promise((resolve, reject) => {
    let done = false, h = null;
    const end = (f, v) => { if (done) return; done = true; clearTimeout(h); f(v); };
    const tick = () => {
      if (done) return;
      clearTimeout(h);
      h = setTimeout(() => {
        // a bulk stream starved by the map / near ones (the browser serves
        // 'high' first) is waiting its turn, not stalled
        if (cls === 'bulk' && _hiIn) { tick(); return; }
        rec.err = 'stall';
        _net.stall++;
        end(reject, new Error(`stalled: no bytes for ${STALL_MS / 1000} s`));
        if (ac) ac.abort();
      }, STALL_MS);
      if (h && h.unref) h.unref();
    };
    tick();
    const init = { priority: HINT[cls], ...(ac ? { signal: ac.signal } : {}) };
    const tu = transportUrl(url);
    new Promise((r) => { const t = _pnow(), f = _fetch(tu, init); _fcAdd(url, cls, _pnow() - t); r(f); }).then(async (r) => {
      if (done) return;
      tick();                                          // the headers are progress
      if (!r.ok) return end(resolve, { st: r.status, raw: null });
      const raw = await _body(r, as, tick);
      end(resolve, { st: r.status, raw });
    }).catch((e) => { if (!done) rec.err = 'net'; end(reject, e); });
  });
}

// the body as `as`, every chunk a progress tick; a response without a
// readable stream (the gates' fakes) is read whole
async function _body(r, as, tick) {
  const rd = r.body && typeof r.body.getReader === 'function' ? r.body.getReader() : null;
  if (!rd) return as === 'json' || as === 'text' ? r.text() : as === 'blob' ? r.blob() : r.arrayBuffer();
  const parts = [];
  let n = 0;
  for (;;) {
    const { done, value } = await rd.read();
    if (done) break;
    parts.push(value);
    n += value.byteLength;
    tick();
  }
  if (as === 'blob') return new Blob(parts, { type: (r.headers && r.headers.get('content-type')) || '' });
  let u8 = parts.length === 1 ? parts[0] : new Uint8Array(n);
  if (parts.length !== 1) { let o = 0; for (const p of parts) { u8.set(p, o); o += p.byteLength; } }
  if (!parts.length) u8 = new Uint8Array(0);
  if (as === 'json' || as === 'text') return new TextDecoder().decode(u8);
  return u8.byteOffset === 0 && u8.byteLength === u8.buffer.byteLength ? u8.buffer : u8.slice().buffer;
}

// _once with bounded retry: a network error, a stall or a retryable status
// tries again after BACKOFF_MS; the last error rejects naming the file
async function _get(url, cls, as, rec) {
  for (let k = 1; ; k++) {
    rec.tries = k;
    const wait = BACKOFF_MS[Math.min(k - 1, BACKOFF_MS.length - 1)];
    let o;
    try {
      o = await _once(url, cls, as, rec);
    } catch (e) {
      const why = (e && e.message) || String(e);
      if (k >= TRIES) {
        _net.fail++;
        console.warn(`[assetq] ${artOf(url)}: giving up after ${k} ${k === 1 ? 'try' : 'tries'} (${why})`);
        throw new Error(`${artOf(url)}: ${why} (${k} ${k === 1 ? 'try' : 'tries'})`);
      }
      _net.retry++;
      console.warn(`[assetq] ${artOf(url)}: ${why} — retry ${k}/${TRIES - 1} in ${wait} ms`);
      await _sleep(wait);
      continue;
    }
    if (o.raw === null && RETRY_ST.has(o.st) && k < TRIES) {
      rec.err = String(o.st);
      _net.retry++;
      console.warn(`[assetq] ${artOf(url)}: HTTP ${o.st} — retry ${k}/${TRIES - 1} in ${wait} ms`);
      await _sleep(wait);
      continue;
    }
    if (o.raw === null && RETRY_ST.has(o.st)) {
      _net.fail++;
      console.warn(`[assetq] ${artOf(url)}: giving up after ${k} tries (HTTP ${o.st})`);
    }
    return o;
  }
}

export function pauseBulk(reason = 'beam') { _paused.add(reason); }
export function resumeBulk(reason = 'beam') { _paused.delete(reason); _pump(); }
export const bulkPaused = () => _paused.size > 0;
export const bulkPending = () => _bulkQ.length + _bulkIn;
// the end of url's last logged request (main.js THE SPRITE GATE): true it
// landed, false it ended without a body (a non-ok answer, a give-up, a
// drop), null queued / in flight / never asked
export function assetEnded(url) {
  for (let i = _log.length - 1; i >= 0; i--) if (_log[i].url === url) return _log[i].t1 === null ? null : _log[i].ok;
  return null;
}

// the browser asked for less data (phones): main.js holds bulk until a card
export const saveDataOn = () => !!(typeof navigator !== 'undefined'
  && navigator.connection && navigator.connection.saveData);

// The wire bytes of a resource-timing entry (R8a). A response the service
// worker answered has transferSize 0 here whether it came from the worker's
// cache or the network; client/sw.js posts {vrmWire: [url, bytes]} for each
// one it FETCHED. One post = one network answer: a URL's posts queue up and
// each is claimed ONCE, by the first entry of that URL asked about without a
// claim (a later cache answer of the same URL keeps its own 0); an entry whose
// post has not landed yet stays unclaimed (transferSize until then).
const _wire = new Map();      // url -> [bytes, …] not yet claimed, in arrival order
const _claim = new Map();     // `${url} ${startTime}` -> the bytes it claimed
export function wireBytes(e) {
  const k = e.name + ' ' + e.startTime;
  const c = _claim.get(k);
  if (c !== undefined) return c;
  const q = _wire.get(e.name);
  if (!q || !q.length) return e.transferSize || 0;
  const b = q.shift();
  _claim.set(k, b);
  return b;
}
// the worker's message (exported for check-serve)
export function onWire(data) {
  const w = data && data.vrmWire;
  if (!Array.isArray(w) || typeof w[0] !== 'string' || !Number.isFinite(w[1]) || w[1] < 0) return;
  const q = _wire.get(w[0]);
  if (q) q.push(w[1]); else _wire.set(w[0], [w[1]]);
}
if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
  try {
    navigator.serviceWorker.addEventListener('message', (ev) => onWire(ev.data));
    navigator.serviceWorker.startMessages();
  } catch { /* no worker: transferSize is the wire */ }
}

// per-class figures for the load report: n requests, bytes (wireBytes of
// the resource timing entries of these very URLs where the browser exposes
// them — the same measure as the report's `bytes` — else the decoded body),
// the longest fetch and the longest queue wait (ms)
// `xfer` = that url -> wire bytes map when the caller has it (reportTables)
export function assetStats(xfer = null) {
  const out = {};
  for (const c of CLASSES) out[c] = { n: 0, bytes: 0, max_ms: 0, wait_ms: 0 };
  if (!xfer) {
    xfer = new Map();
    if (typeof performance !== 'undefined' && performance.getEntriesByType) {
      for (const e of performance.getEntriesByType('resource')) xfer.set(e.name, wireBytes(e));
    }
  }
  for (const r of _log) {
    const o = out[r.cls];
    o.n++;
    const tb = xfer.size ? xfer.get(r.abs) : undefined;
    o.bytes += typeof tb === 'number' ? tb : r.bytes;
    if (r.t1 !== null && r.t0 !== null) o.max_ms = Math.max(o.max_ms, r.t1 - r.t0);
    if (r.t0 !== null) o.wait_ms = Math.max(o.wait_ms, r.t0 - r.q);
  }
  return out;
}

// the raw request log (console: window.__vr.assets())
export const assetLog = () => _log.slice();

// a request's artifact name: the path after /bake/<id>/ (else the path)
function artOf(url) {
  const u = String(url).replace(/[?#].*$/, '');
  const i = u.indexOf('/bake/');
  if (i < 0) return u.replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/^\/+/, '').slice(0, 80);
  const p = u.slice(i + 6);
  return p.slice(p.indexOf('/') + 1).slice(0, 80);
}
const sig = (x) => (x === null || !Number.isFinite(x) ? null : Number(x.toPrecision(4)));

// the load report's `fam` block (load-bytes-by-family): transfer bytes of the
// resource-timing entries summed per artifact family — the tiled families by
// path prefix, the boot set's world-wide files by name, the client's ground
// material arrays (/materials/) — so a boot-set change is provable per file.
// The keys are THIS closed set (gs.js cleanLoad FAM_KEYS takes no other);
// a /bake/ path no rule names is `other`, a URL outside /bake/ and
// /materials/ is not counted.
export const FAM_FILES = ['meta', 'ways', 'trees', 'shrubs', 'facades', 'mappack',
  'horizon', 'structures', 'structures_ground', 'spans', 'water', 'rivers', 'objects', 'lifts', 'livery',
  'gazetteer', 'buildings_far', 'buildings_index', 'registry', 'shopsigns'];
export const FAM_KEYS = ['groundm', 'terrain', 'surf_coarse', 'surf_tiles', 'surf_shade', 'surf_ways', 'ring', 'buildings', 'bld_col',
  'veg', 'trails', 'materials', ...FAM_FILES, 'other'];
const FAM_NAMED = new Set(FAM_FILES);
export function famOf(url) {
  const u = String(url).replace(/[?#].*$/, '');
  const i = u.indexOf('/bake/');
  if (i < 0) return /(^|\/)materials\//.test(u) ? 'materials' : null;   // groundmat.js asks `materials/...` relative
  const p = u.slice(i + 6), path = p.slice(p.indexOf('/') + 1);
  let m = /^surface\/(tiles|shade|ways)\//.exec(path);
  if (m) return 'surf_' + m[1];
  if (path === 'surface/coarse.png') return 'surf_coarse';
  if (/^buildings\/col_/.test(path)) return 'bld_col';
  // the building dir's world-wide files (facades.png the atlas, registry.json)
  m = /^buildings\/([a-z_]+)\.[a-z0-9]+$/.exec(path);
  if (m && FAM_NAMED.has(m[1])) return m[1];
  // the ways (R0 WAY1): the slim doc + the way1/ tiles
  if (/^way1\//.test(path) || path === 'ways_slim.json') return 'ways';
  if (/^crowns\//.test(path)) return 'trees';     // the tree crown packs (shrubs/: the shrubs')
  m = /^(groundm|terrain|buildings|veg|trails|ring|trees|shrubs)\//.exec(path);
  if (m) return m[1];
  if (/^ring[-_.]/.test(path)) return 'ring';     // ring.bin, ring-<sha>.bin (sector meshes)
  m = /^([a-z_]+)\.[a-z0-9]+$/.exec(path);
  return m && FAM_NAMED.has(m[1]) ? m[1] : 'other';
}
// {family: {n requests, b wire bytes}} over resource-timing entries
// ({name, transferSize}, wireBytes; an /o/<sha> name counts as the file it carries); only the families seen.
// `names` / `wb` = the entries' logicalUrl names / wire bytes when the caller
// has them (reportTables: each entry resolved once for every use)
export function familyBytes(entries, names = null, wb = null) {
  const out = {};
  const es = entries || [];
  for (let k = 0; k < es.length; k++) {
    const e = es[k];
    const f = famOf(names ? names[k] : logicalUrl(e.name));
    if (!f) continue;
    const o = out[f] || (out[f] = { n: 0, b: 0 });
    o.n++;
    o.b += wb ? wb[k] : wireBytes(e);
  }
  return out;
}

// THE LOAD REPORT'S PER-ENTRY TABLES IN STEPS (R8 M5c; main.js
// loadReportSteps): each resource-timing entry's logical name and wire bytes
// once, REPORT_STEP entries a step, + the url -> bytes map assetStats reads.
// The same calls in entry order as the one-shot report made them (a url's
// wire posts are claimed in entry order either way)
export const REPORT_STEP = 1024;
export function* reportTables(entries, step = REPORT_STEP) {
  const n = entries.length, names = new Array(n), wb = new Array(n), xfer = new Map();
  for (let k = 0; k < n; k++) {
    const e = entries[k];
    names[k] = logicalUrl(e.name);
    wb[k] = wireBytes(e);
    xfer.set(e.name, wb[k]);
    if ((k + 1) % step === 0 && k + 1 < n) yield k + 1;
  }
  return { names, wb, xfer };
}

// A REPORT SENT IN STEPS (R8 M5c): sendInSteps runs a report generator with
// `run` (main.js: decode.js runSteps) and hands the value to send(data,
// false); a page that is LEAVING (pagehide / hidden: main.js armPreEntryReport)
// calls flushReports, which drains every report still in steps HERE and
// sends it in this very task — send(data, true): the beacon — so no report
// is lost to a page that left mid-steps (check-serve 'M5c'). A build that
// throws is warned and dropped, as the one-shot's was.
const _pend = new Set();
export function sendInSteps(it, label, send, run) {
  const p = { it, send, done: false };
  _pend.add(p);
  run(it, label).then((v) => {
    if (p.done) return;
    p.done = true; _pend.delete(p);
    send(v, false);
  }, (e) => {
    if (p.done) return;
    p.done = true; _pend.delete(p);
    console.warn('[load] report not built:', e);
  });
}
export function flushReports() {
  for (const p of [..._pend]) {
    p.done = true; _pend.delete(p);
    let v;
    try { let s; do s = p.it.next(); while (!s.done); v = s.value; } catch (e) { console.warn('[load] report not built:', e); continue; }
    p.send(v, true);
  }
}

// the load report's need block (buildLoadReport `need`): the bulk class's
// re-score count, drops (n, and the bytes the drops' needs declared, 0 when
// unknown), the
// queue wait p50 / max (ms), the robust-download counts over EVERY class
// (retry_n retries, stall_n stalls aborted, fail_n requests given up), and
// the `rows` longest-waiting bulk fetches:
// {n artifact, s score at the start (px / byte), w wait ms, ms fetch ms,
// b bytes, v in view at the start (1/0), d dropped (1/0)}
export function needReport(rows = 24) {
  const bulk = _log.filter((r) => r.cls === 'bulk');
  const waits = bulk.filter((r) => r.t0 !== null && !r.dropped).map((r) => r.t0 - r.q).sort((a, b) => a - b);
  const drops = bulk.filter((r) => r.dropped);
  const top = bulk.filter((r) => r.t0 !== null).sort((a, b) => (b.t0 - b.q) - (a.t0 - a.q)).slice(0, rows);
  return {
    n: bulk.length, rescores: _rescores,
    drop_n: drops.length, drop_b: drops.reduce((a, r) => a + (r.nb || 0), 0),
    retry_n: _net.retry, stall_n: _net.stall, fail_n: _net.fail,
    wait_p50: waits.length ? Math.round(waits[(waits.length - 1) >> 1]) : null,
    wait_max: waits.length ? Math.round(waits[waits.length - 1]) : null,
    rows: top.map((r) => ({ n: artOf(r.url), s: sig(r.s), w: Math.round(r.t0 - r.q),
      ms: r.t1 !== null ? Math.round(r.t1 - r.t0) : null, b: r.bytes, v: r.v ? 1 : 0, d: r.dropped ? 1 : 0 })),
  };
}
